import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { OfferCategory } from '@cds/betting-offer';
import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    BetBuilderButtonSource,
    CrmPromoIdsMap,
    EventChangePayload,
    EventModel,
    EventOptionGroup,
    EventOptionGroupType,
} from '@frontend/sports/betting-offer/feature/model';
import { AppConfig, EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { DetailedEventSubscriptionService } from '@frontend/sports/event-subscription/feature/detailed-event-subscription';
import { BottomOffsetService } from '@frontend/sports/layout/feature';
import { ActiveModal, PopupAction, PopupClosingResult } from '@frontend/sports/modal/feature';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { PopupManager } from '@frontend/sports/popup/feature';
import { trackingConstants } from '@frontend/sports/tracking/feature';
import { OptionPanelThreshold } from '@frontend/sports/types/configuration';
import { HtmlNode, LoadingIndicatorService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';

import { EpcotNotificationComponent } from '../common/epcotNotifications/notification.component';
import { OptionMarketSetData } from '../event-details-common/event-details-common.models';
import { EventDetailsService } from '../event-details-common/event-details.service';
import { OptionGroupTrackingService } from '../option-group/option-group-tracking.service';
import { OptionPanelComponent } from '../option-group/option-panel.component';
import { TabBarEvent, TabBarItem } from '../tab-bar/tab-bar.models';
import { UiManagerActions } from '../ui-manager/ui-manager.actions';
import { UiStates } from '../ui-manager/ui-states.enum';
import { OptionSheetViewDataService } from './option-sheet-view-data.service';
import { OptionSheetViewService } from './option-sheet-view.service';
import { SheetViewCarouselStateService } from './sheet-view-carousel/sheet-view-carousel-state.service';
import { SheetViewCarouselTrackingService, SheetViewCarouselTrackingType } from './sheet-view-carousel/sheet-view-carousel-tracking.service';
import { SheetViewCarouselComponent } from './sheet-view-carousel/sheet-view-carousel.component';
import { OptionSheetViewActions } from './sheet-view.actions';

@Component({
    selector: 'ms-option-sheet-view',
    templateUrl: './option-sheet-view.component.html',
    styleUrl: './option-sheet-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SheetViewCarouselComponent, OptionPanelComponent, EpcotNotificationComponent],
    providers: [SheetViewCarouselTrackingService],
    host: {
        '[style.padding-bottom.px]': `this.bottomOffset()`,
        '[class.epcot-enabled]': 'isEpcotEnabled',
    },
})
export class OptionSheetViewComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() sourceEvent: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() promoted: boolean;
    @Input() crmPromoted: boolean;
    @Input() sourceOptionGroup: EventOptionGroup;
    @Input() thresholds?: OptionPanelThreshold[];
    @Input() crmPromoIdsMap: CrmPromoIdsMap = { markets: {}, options: {}, templateIds: {} };
    @Input() toggleable = true;
    @Input() showHelp = false;
    @Input() showEventName = false;
    @Input() showMarketHelp = false;
    @Input() showEventTimer = false;
    @Input() isLinkToEvent = true;
    @Input() showEventChevron = false;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() showMoreCounter = false;
    @Input() optionsLimitOverride?: number;
    @Input() babButtonSource?: BetBuilderButtonSource;
    @Input() betBuilderEpcotEnabled = false;
    @Input() hideMarketHeader: boolean;
    @Input() showSixPackLogosColor: boolean;
    @Input() showSixPackLogos: boolean;
    @Input() selectedSetId: string | number;
    @Input() isInSheetView = true;
    @Input() hideEventCarousel?: boolean;
    @Input() groupType: OptionGroupType;
    @Input() optionMarketSetData: OptionMarketSetData;
    @Input() dynamicOfferCategory?: string;
    @Input() subscribeSourceEvent = false;
    private epcotConfig = inject(EpcotConfig);

    activeEvent = signal<EventModel | undefined>(undefined);
    activeOptionGroup = signal<EventOptionGroup | undefined>(undefined);
    hideOptions = signal<boolean>(false);
    isCarouselApplicable = signal<boolean>(false);
    // this is returning a string because we want this to be "undefined" by default
    protected bottomOffset = computed<string>(() => (this.bottomOffsetService.bottomOffset() ? `${this.bottomOffsetService.bottomOffset()}` : ''));
    private isCarouselVisible = signal<boolean>(false);
    private eventSubscription: EventSubscription<EventModel>;
    public isEpcotEnabled = this.epcotConfig.isEnabled;
    activeEvents = signal<{ [eventId: string]: EventModel }>({});

    constructor(
        public sitecore: Sitecore,
        private activeModal: ActiveModal,
        private htmlNode: HtmlNode,
        private optionGroupTrackingService: OptionGroupTrackingService,
        private optionSheetViewService: OptionSheetViewService,
        private optionSheetViewDataService: OptionSheetViewDataService,
        private popupManager: PopupManager,
        private appConfig: AppConfig,
        private sheetViewCarouselStateService: SheetViewCarouselStateService,
        private sheetViewCarouselTrackingService: SheetViewCarouselTrackingService,
        private store: Store,
        private bottomOffsetService: BottomOffsetService,
        private loadingIndicatorService: LoadingIndicatorService,
        private eventDetailsService: EventDetailsService,
        private detailedSubscriptionService: DetailedEventSubscriptionService,
        private destroyRef: DestroyRef,
    ) {
        this.sheetViewCarouselStateService
            .getShowEventCarousel()
            .pipe(takeUntilDestroyed())
            .subscribe((val: boolean) => {
                this.isCarouselVisible.set(val);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.sourceOptionGroup) {
            if (!this.activeOptionGroup() || this.activeOptionGroup()?.id === this.sourceOptionGroup.id) {
                this.activeOptionGroup.set(this.sourceOptionGroup);
                this.setHideOptions();
            }
        }
    }

    ngOnInit(): void {
        this.isCarouselApplicable.set(this.verifyCarouselApplicability());

        if (this.subscribeSourceEvent) {
            this.setSubscription(this.sourceEvent);
        }

        this.updateUIState(true);
        this.trackEvent(trackingConstants.LOAD, trackingConstants.EVENT_CONTENT_VIEW);
    }

    onActivePeriodChange(event: TabBarEvent): void {
        if (!this.isCarouselVisible()) {
            return;
        }

        const tabs: TabBarItem[] =
            this.activeOptionGroup()
                ?.getSubGroups(EventOptionGroupType.Period)
                .map((og) => {
                    return {
                        id: og.detailedGrouping.periodGroup || 0,
                        title: og.periodName || '',
                        active: false,
                        attribute: og.id.split('-').pop(),
                    };
                }) ?? [];

        const tabIndex = tabs.findIndex((p) => p.id === event.active.id);
        this.sheetViewCarouselTrackingService.trackEventCarousel(
            SheetViewCarouselTrackingType.TabBarClick,
            this.activeEvent() || this.sourceEvent,
            this.activeOptionGroup()?.name || this.sourceOptionGroup.name,
            event.active.title,
            tabIndex,
        );
    }

    ngAfterViewInit(): void {
        if (this.appConfig.modalDialogGesturesEnabled) {
            this.activeModal?.result.then((data?: PopupClosingResult) => {
                if (data && data.closeAction === PopupAction.CloseByPan) {
                    this.updateUIState(false);
                }
            });
        }
    }

    closeBtnClicked(): void {
        this.activeModal.close();
        this.updateUIState(false);
        this.store.dispatch(OptionSheetViewActions.closeOptionSheetView());
        this.trackEvent(trackingConstants.CLOSE, trackingConstants.EVENT_TRACKING);
    }

    eventChange({ event, tileIndex }: EventChangePayload) {
        if (event.id == this.sourceEvent.id && !this.subscribeSourceEvent) {
            this.setActiveEvent({ event: this.sourceEvent, optionGroup: this.sourceOptionGroup, tileIndex });
            return;
        }

        const loadingIndicatorHandler = this.loadingIndicatorService.start();
        const marketGroupItemIds = this.sourceOptionGroup.detailedGrouping.marketGroupItemId;

        this.optionSheetViewDataService
            .getFixture(event, marketGroupItemIds, this.groupType, this.dynamicOfferCategory)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((eventDetails) => {
                loadingIndicatorHandler?.done();

                if (!eventDetails) {
                    this.setActiveEvent({
                        event,
                        optionGroup: this.optionSheetViewDataService.getEmptyOptionGroup(this.sourceOptionGroup),
                        tileIndex,
                    });
                    return;
                }

                this.setOptionGroupData(eventDetails, tileIndex);
                this.setSubscription(eventDetails);
            });
    }

    private setActiveEvent({ event, optionGroup, tileIndex }: { event: EventModel; optionGroup: EventOptionGroup; tileIndex?: number }): void {
        this.activeEvent.set(event);
        this.activeOptionGroup.set(optionGroup);

        if (tileIndex !== undefined) {
            this.sheetViewCarouselTrackingService.trackEventCarousel(
                SheetViewCarouselTrackingType.EventCarouselClick,
                event,
                optionGroup.name,
                optionGroup.periodName,
                tileIndex,
            );
        }
        this.setHideOptions();
    }

    private setOptionGroupData(event: EventModel, tileIndex?: number): void {
        // need this filtering for data via websocket
        event.optionGroups = this.filterOptionGroups(event.optionGroups || []);

        const optionGroups = this.eventDetailsService.prepareEventOptionGroups({
            event: event,
            ...this.optionMarketSetData,
        });

        const updatedOptionGroups = optionGroups?.[0] || this.optionSheetViewDataService.getEmptyOptionGroup(this.sourceOptionGroup);
        this.setActiveEvent({ event, optionGroup: updatedOptionGroups, tileIndex });
    }

    private filterOptionGroups(optionGroups: EventOptionGroup[]): EventOptionGroup[] {
        if (this.dynamicOfferCategory) {
            return optionGroups.filter((op) => op.dynamicCategories?.includes(this.dynamicOfferCategory!));
        } else {
            return optionGroups.filter(
                (op) =>
                    op.detailedGrouping?.marketGroupItemId === this.sourceOptionGroup.detailedGrouping?.marketGroupItemId &&
                    op.detailedGrouping?.marketOrder === this.sourceOptionGroup.detailedGrouping?.marketOrder,
            );
        }
    }

    private setSubscription(event: EventModel): void {
        this.eventSubscription?.unsubscribe();
        this.activeEvents.update((events) => ({
            ...events,
            [event.id]: event,
        }));
        this.eventSubscription = this.detailedSubscriptionService.subscribe([
            {
                event: event,
                callback: (updatedEvent: EventModel) => {
                    if (updatedEvent.id in this.activeEvents()) {
                        this.updateEvent(updatedEvent);
                    }
                },
            },
        ]);
    }

    private updateEvent(updatedEvent: EventModel): void {
        if (updatedEvent.id === (this.activeEvent() || this.sourceEvent).id) {
            this.setOptionGroupData(updatedEvent);
        }
    }

    private verifyCarouselApplicability(): boolean {
        if (
            !!this.hideEventCarousel ||
            this.sourceOptionGroup.templateCategory === OfferCategory.Outrights ||
            this.groupType === OptionGroupType.PrecreatedBuildABet
        ) {
            return false;
        }

        return this.optionSheetViewService.isCarouselEnabled(
            this.sourceEvent.league.parentLeagueId ?? this.sourceEvent.league.id,
            this.sourceEvent.sport.id,
        );
    }

    private setHideOptions(): void {
        this.hideOptions.set(!this.activeOptionGroup()?.options?.length && !this.activeOptionGroup()?.subGroups?.length);
    }

    private updateUIState(isOpen: boolean): void {
        if (!this.popupManager.isOpen()) {
            if (isOpen) {
                this.store.dispatch(UiManagerActions.addState({ state: UiStates.SheetViewOpened }));
            } else {
                this.store.dispatch(UiManagerActions.removeState({ state: UiStates.SheetViewOpened }));
            }
            this.htmlNode.setCssClass('sheet-view-for-market', isOpen);
        }
        this.htmlNode.setCssClass('sheet-view-for-market-for-sgp', isOpen);
    }

    private getLocationEvent(): string {
        let locationEventInfo = this.sourceOptionGroup.name;
        const trackingInfo = this.tracking?.split('/')[0];
        if (trackingInfo) {
            locationEventInfo = `${trackingInfo}-${locationEventInfo}`;
        }

        return locationEventInfo;
    }

    private trackEvent(actionEvent: string, event: string): void {
        this.optionGroupTrackingService.trackEvents({
            label: this.sourceEvent.sport.name,
            actionEvent: actionEvent,
            participants: this.sourceEvent.participants,
            locationEvent: this.getLocationEvent(),
            eventDetailText: trackingConstants.SEE_ALL_OVERLAY,
            event: event,
            sportId: this.sourceEvent.sport.id,
            leagueId: this.sourceEvent.league.id,
        });
    }

    ngOnDestroy() {
        this.eventSubscription?.unsubscribe();
    }
}
